import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { bucketToTarget } from "@/components/demand-chart/aggregation";
import { ChartCanvas } from "@/components/demand-chart/ChartCanvas";
import { ChartToolbar } from "@/components/demand-chart/ChartToolbar";
import { OverviewBrush } from "@/components/demand-chart/OverviewBrush";
import {
  baselinePoints,
  forecastBandPoints,
  forecastMeanDaily,
  forecastMeanPoints,
  metricPoints,
  trendPoints,
} from "@/components/demand-chart/series";
import type {
  Aggregation,
  Point,
  ViewRange,
} from "@/components/demand-chart/types";
import { useDemandChartView } from "@/components/demand-chart/useDemandChartView";
import { parseDate } from "@/utils/formatters";

import { useChartData } from "./useChartData";

const DAY_MS = 24 * 60 * 60 * 1000;

// The y-axis unit is "demand per period". Each aggregation maps to a day count.
const PERIOD_DAYS_BY_AGG: Record<Aggregation, number> = {
  week: 7,
  month: 30,
  quarter: 90,
};
// Gaussian trend smoothing window (days) — fixed; shared by both demand views.
const TREND_WINDOW_DAYS = 120;

type DemandChartProps = {
  itemId: number;
  locationIds: number[];
  forecastEnabled: boolean;
};

export default function DemandChart({
  itemId,
  locationIds,
  forecastEnabled,
}: DemandChartProps) {
  const { t } = useTranslation();
  const view = useDemandChartView();
  const { aggregation, metric, mode, activePreset } = view;
  const periodDays = PERIOD_DAYS_BY_AGG[aggregation];
  const { historyQuery, forecastQuery, buckets } = useChartData(
    itemId,
    locationIds,
    mode,
  );

  // A forecast failure must not blank the chart — history still renders.
  const payload = forecastQuery.data ?? null;
  const showForecast = forecastEnabled && (payload?.forecast?.length ?? 0) > 0;

  // Daily metric series (raw) — source for the nav sparkline and range totals.
  const daily = useMemo<Point[]>(
    () => metricPoints(buckets, metric),
    [buckets, metric],
  );

  // Full data span (epoch-ms); max extends to the last forecast point.
  const dataSpan = useMemo<ViewRange>(() => {
    if (daily.length === 0) {
      const now = Date.now();
      return { min: now - 365 * DAY_MS, max: now };
    }
    const times = daily.map((p) => parseDate(p.x).getTime());
    const historyMax = Math.max(...times);
    const fcPts = payload?.forecast ?? [];
    const fcMax =
      fcPts.length > 0
        ? parseDate(fcPts[fcPts.length - 1].date).getTime()
        : -Infinity;
    return { min: Math.min(...times), max: Math.max(historyMax, fcMax) };
  }, [daily, payload]);

  // The forecast starts on the tenant's today. Without one there is no today
  // line; presets anchor on the history's end, which is also today.
  const todayValue = payload?.forecast?.[0]?.date;
  const nowMs = todayValue ? parseDate(todayValue).getTime() : undefined;
  const visible = view.resolveView(dataSpan, nowMs ?? dataSpan.max);

  // Datasets. Order is load-bearing: the band datasets (3 & 4) fill toward the
  // forecast-mean dataset (index 2). Datasets 2–4 are always present (empty when
  // the forecast is off) so the band's `fill: "2"` index never shifts.
  const data = useMemo(() => {
    const band = showForecast
      ? forecastBandPoints(payload!.forecast, periodDays)
      : { upper: [], lower: [] };
    return {
      datasets: [
        {
          // 0 — rough per-period rolling-sum baseline
          label: t("items.detail.demandChart.datasets.baseline"),
          data: baselinePoints(buckets, metric, periodDays),
          borderColor: "#d4d4d8",
          borderWidth: 1,
          pointRadius: 0,
        },
        {
          // 1 — Gaussian-smoothed per-period trend
          label: t("items.detail.demandChart.datasets.trend"),
          data: trendPoints(buckets, metric, periodDays, TREND_WINDOW_DAYS),
          borderColor: "#f27a61",
          borderWidth: 2.5,
          pointRadius: 0,
          tension: 0.3,
        },
        {
          // 2 — forecast mean (the band fills toward this)
          label: t("items.detail.demandChart.datasets.forecast"),
          data: showForecast
            ? forecastMeanPoints(payload!.forecast, periodDays)
            : [],
          borderColor: "#f27a61",
          borderDash: [5, 4],
          borderWidth: 2.5,
          pointRadius: 0,
          tension: 0.3,
        },
        {
          // 3 — upper confidence band
          label: t("items.detail.demandChart.datasets.upperBand"),
          data: band.upper,
          borderWidth: 0,
          pointRadius: 0,
          fill: "2",
          backgroundColor: "rgba(242,122,97,0.12)",
        },
        {
          // 4 — lower confidence band
          label: t("items.detail.demandChart.datasets.lowerBand"),
          data: band.lower,
          borderWidth: 0,
          pointRadius: 0,
          fill: "2",
          backgroundColor: "rgba(242,122,97,0.12)",
        },
      ],
    };
  }, [t, buckets, metric, periodDays, payload, showForecast]);

  // Nav sparkline: coarse history + coarse forecast tail.
  const navHistory = useMemo(() => bucketToTarget(daily), [daily]);
  const navForecast = useMemo(
    () =>
      showForecast ? bucketToTarget(forecastMeanDaily(payload!.forecast)) : [],
    [payload, showForecast],
  );

  const dailyForecast = useMemo<Point[]>(
    () => (showForecast ? forecastMeanDaily(payload!.forecast) : []),
    [payload, showForecast],
  );

  return (
    <div className="flex flex-col gap-3">
      <ChartCanvas
        data={data}
        dataSpan={dataSpan}
        viewRange={visible}
        aggregation={aggregation}
        metric={metric}
        todayValue={todayValue}
        dailyHistory={daily}
        dailyForecast={dailyForecast}
        isLoading={historyQuery.isLoading}
        onUserRange={view.handleUserRange}
        height={420}
      />

      <OverviewBrush
        series={navHistory}
        forecastSeries={navForecast}
        nowMs={nowMs}
        viewRange={visible}
        rangeMax={dataSpan.max}
        onViewRangeChange={(r) => {
          view.setActivePreset(null);
          view.setViewRange(r);
        }}
        onReset={view.reset}
      />

      <div className="mt-1 border-t pt-3">
        <ChartToolbar
          activePreset={activePreset}
          onPreset={view.handlePreset}
          aggregation={aggregation}
          onAggregation={view.setAggregation}
          mode={mode}
          onMode={view.setMode}
        />
      </div>
    </div>
  );
}
